import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useCITranslate } from '../../i18n/customerIdentity';
import { useCiAdminBasePath } from '../../contexts/CiAdminAuthContext';
import CiPageChrome from '../../components/CustomerIdentity/CiPageChrome';
import ContactsDirectoryFilters from '../../components/CustomerIdentity/ContactsDirectoryFilters';
import UsersDirectoryTable from '../../components/CustomerIdentity/UsersDirectoryTable';
import { CIInfoBar } from '../../components/CustomerIdentity/CustomerIdentityShared';
import { listCustomerUsersPaginated } from '../../services/customerIdentityApi';

const PAGE_SIZE = 25;

const SORT_KEYS = {
  email: 'email',
  name: 'first_name',
  role: 'role',
  status: 'status',
  last_login_at: 'last_login_at',
  customer: 'customer_name',
  site: 'site_name',
};

// Cross-customer, cross-site Contacts directory (customer_identity_users is
// the "Contacts" entity). Same backend/data as the "Users" page, but this
// page always stays in place when a customer filter is picked, instead of
// redirecting into that customer's detail tab -- it's meant for browsing
// contacts across many customers at once, not as a funnel into one
// customer's record. The customer filter is a type-ahead search (see
// CustomerSearchPicker / ContactsDirectoryFilters), not a dropdown
// preloaded with every customer on the account.
export default function ContactsDirectory() {
  const t = useCITranslate();
  const basePath = useCiAdminBasePath();
  const [searchParams] = useSearchParams();
  const siteFilter = searchParams.get('site_id') || '';
  const querySuffix = searchParams.toString() ? `?${searchParams.toString()}` : '';

  const [filterCustomerId, setFilterCustomerId] = useState('');
  const [filterCustomerLabel, setFilterCustomerLabel] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState('email');
  const [sortDir, setSortDir] = useState('asc');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageMessage, setPageMessage] = useState(null);

  const prevSiteRef = useRef(null);
  useEffect(() => {
    if (prevSiteRef.current === null) {
      prevSiteRef.current = siteFilter;
      return;
    }
    if (prevSiteRef.current !== siteFilter) {
      setFilterCustomerId('');
      setFilterCustomerLabel('');
      prevSiteRef.current = siteFilter;
    }
  }, [siteFilter]);

  useEffect(() => {
    setPage(0);
  }, [siteFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const apiSort = SORT_KEYS[sortKey] || 'email';
      const params = {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        sort_by: apiSort,
        sort_dir: sortDir,
        search: searchApplied.trim() || undefined,
      };
      if (siteFilter) params.site_id = siteFilter;
      if (filterCustomerId) params.customer_id = filterCustomerId;
      const data = await listCustomerUsersPaginated(params);
      setRows(data?.items || []);
      setTotal(Number(data?.total) || 0);
    } catch (err) {
      setPageMessage({
        kind: 'danger',
        i18nKey: 'directoryUsers.couldNotLoad',
        error: err,
      });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [siteFilter, page, sortKey, sortDir, searchApplied, filterCustomerId]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil((total || 0) / PAGE_SIZE));

  const onSort = (key) => {
    setPage(0);
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const applySearch = () => {
    setPage(0);
    setSearchApplied(searchDraft.trim());
  };

  const handleCustomerSelect = (id, label) => {
    setPage(0);
    setFilterCustomerId(id);
    setFilterCustomerLabel(label);
  };

  const handleCustomerClear = () => {
    setPage(0);
    setFilterCustomerId('');
    setFilterCustomerLabel('');
  };

  const showSiteColumn = !siteFilter;

  return (
    <div className="d-flex flex-column h-100">
      <CIInfoBar message={pageMessage} onDismiss={() => setPageMessage(null)} />
      <CiPageChrome
        title="Contacts"
        subtitle="Browse contacts across customers, filter by customer, and search by contact or customer/site fields."
      >
        <div className="container-fluid px-0 px-md-1">
          <ContactsDirectoryFilters
            siteId={siteFilter}
            filterCustomerId={filterCustomerId}
            filterCustomerLabel={filterCustomerLabel}
            onCustomerSelect={handleCustomerSelect}
            onCustomerClear={handleCustomerClear}
            searchDraft={searchDraft}
            onSearchDraftChange={setSearchDraft}
            onApplySearch={applySearch}
            loading={loading}
          />
          <UsersDirectoryTable
            t={t}
            basePath={basePath}
            querySuffix={querySuffix}
            rows={rows}
            loading={loading}
            total={total}
            page={page}
            totalPages={totalPages}
            showSiteColumn={showSiteColumn}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={onSort}
            onPrevPage={() => setPage((p) => Math.max(0, p - 1))}
            onNextPage={() => setPage((p) => p + 1)}
          />
        </div>
      </CiPageChrome>
    </div>
  );
}
